import { errorAlert, toUserAlert, wsEnvelope, } from '@xdocs/schemas';
import { ZodError } from 'zod';
/**
 * Typed envelope codec (E17 §5.1). ONE place that turns a `WsEnvelope` into
 * wire bytes and back — every op handler and the client state machine go
 * through these two functions, never `JSON.stringify`/`JSON.parse` directly.
 */
/** Catalog key surfaced when a frame can't be parsed as JSON at all. */
export const MALFORMED_FRAME_KEY = 'errors:ws.malformed_frame';
/**
 * Encodes an outgoing envelope. Validates against {@link wsEnvelope} first —
 * an invalid outgoing frame is a programmer error (e.g. a missing `ts`), so
 * this throws a `ZodError` rather than silently sending garbage.
 */
export function encodeEnvelope(frame) {
    const parsed = wsEnvelope.parse(frame);
    return JSON.stringify(parsed);
}
/**
 * Decodes an incoming wire frame. Never throws — malformed JSON or a schema
 * violation both come back as `{ ok: false, alert }` so callers (client
 * `onmessage` / server op router) can log + drop the frame instead of
 * crashing the socket.
 */
export function decodeEnvelope(raw) {
    let json;
    try {
        json = JSON.parse(raw);
    }
    catch {
        return {
            ok: false,
            alert: { code: 'VALIDATION', message: MALFORMED_FRAME_KEY, fields: [] },
        };
    }
    const result = wsEnvelope.safeParse(json);
    if (!result.success) {
        return { ok: false, alert: toUserAlert(result.error) };
    }
    return { ok: true, envelope: result.data };
}
/** Builds a well-formed `req` frame. */
export function buildReqEnvelope(input) {
    return {
        type: 'req',
        id: input.id,
        op: input.op,
        payload: input.payload,
        ts: input.ts ?? Date.now(),
    };
}
/** Builds a well-formed `res` frame (same `id` as the originating `req`). */
export function buildResEnvelope(id, payload, ts = Date.now()) {
    return { type: 'res', id, payload, ts };
}
/** Builds a well-formed `err` frame (same `id` as the originating `req`). */
export function buildErrEnvelope(id, alert, ts = Date.now()) {
    return { type: 'err', id, payload: alert, ts };
}
/** Builds a `push` frame (no `id` — server-initiated, channel-addressed). */
export function buildPushEnvelope(channel, payload, ts = Date.now()) {
    return { type: 'push', channel, payload, ts };
}
/**
 * Conventional payload key carrying the `ws_notifications_outbox` row id on
 * a push frame (E20 §5.10-1 resume cursor).
 *
 * OPEN ISSUE (cross-package): the frozen `wsEnvelope` schema (§5.1) has no
 * dedicated field for this — `id` is UUID-only, but the outbox row id is
 * very likely a monotonic bigserial (`cross.ts` comment: "bigserial/uuid";
 * `resumePayload.lastSeenOutboxId` is an *unconstrained* string, not
 * `.uuid()`, which only makes sense if it isn't one), and a UUID has no
 * "newer than" ordering a `WHERE id > $cursor` replay query needs anyway.
 * Until `@xdocs/schemas` grows a dedicated field, the fan-out writer
 * (`@xdocs/db` `publish()`) should stash the outbox row id at
 * `payload[OUTBOX_ID_PAYLOAD_KEY]` alongside the push's own domain payload;
 * {@link extractOutboxId} reads it from there, falling back to `envelope.id`
 * for the (less likely) case outbox ids do end up UUID-shaped.
 */
export const OUTBOX_ID_PAYLOAD_KEY = '_outboxId';
/** Reads the resume cursor id off a `push` envelope, or `undefined` if absent. */
export function extractOutboxId(envelope) {
    if (envelope.type !== 'push')
        return undefined;
    const payload = envelope.payload;
    if (payload && typeof payload === 'object' && OUTBOX_ID_PAYLOAD_KEY in payload) {
        const value = payload[OUTBOX_ID_PAYLOAD_KEY];
        if (typeof value === 'string')
            return value;
    }
    return envelope.id;
}
/** Builds the `err BAD_OP` reply for an unrecognized `op` (§5.1). */
export function buildBadOpEnvelope(id, ts = Date.now()) {
    return buildErrEnvelope(id, { code: 'BAD_OP', message: 'errors:ws.bad_op' }, ts);
}
/**
 * Classifies an arbitrary caught error into the closed {@link UserAlert}
 * shape (E12 §16). `ZodError` maps through {@link toUserAlert}; anything else
 * becomes a generic `INTERNAL` alert — never a raw stack/message leak.
 */
export function classifyError(err) {
    if (err instanceof ZodError)
        return toUserAlert(err);
    const parsed = errorAlert.safeParse(err);
    if (parsed.success && parsed.data.code !== 'CONTENT_POLICY_VIOLATION') {
        return parsed.data;
    }
    return { code: 'INTERNAL', message: 'errors:internal.generic', fields: [] };
}
/** Type guard: is this frame type one that always carries a correlation `id`. */
export function requiresCorrelationId(type) {
    return type === 'req' || type === 'res' || type === 'err';
}
